import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isDestroying, isDestroyed, registerDestructor } from '@ember/destroyable';
import { dropTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import { trackedFunction } from 'reactiveweb/function';
import {
  PhArrowCounterClockwise,
  PhArrowLeft,
  PhCaretLeft,
  PhDownloadSimple,
  PhFunnel,
  PhSlidersHorizontal,
  PhSquaresFour,
} from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Select from 'tio-ui/components/select';
import { findRecord } from '@warp-drive/utilities/json-api';
import WidgetGrid from '../dashboard-builder/widget-grid.gts';
import WidgetRenderer from '../dashboard-builder/widget-renderer.gts';
import FilterEditor from '../dashboard-builder/filter-editor.gts';
import DrilldownChips from './drilldown-chips.gts';
import DashboardUnavailable from './unavailable.gts';
import { resolveDatasetFields } from '../../utils/dashboard-builder/widget-resolver.ts';
import {
  SQL_SCOPE_REQUIRED_MESSAGE,
  sqlScopeUnavailable,
} from '../../utils/dashboard-builder/widget-data-adapter.ts';
import {
  exportWidgetCsv,
  WidgetExportUnavailableError,
} from '../../utils/dashboard-builder/widget-export.ts';
import {
  widgetExportBlocker,
  widgetExportDefinition,
} from '../../utils/dashboard-builder/widget-export-definition.ts';
import { loadActiveCompanyOptions } from '../../utils/company-options.ts';
import {
  fetchDistinctValues,
  shouldFetchDistinctValues,
} from '../../utils/distinct-values-resolver.ts';
import {
  getCompanyFilterField,
  getModelTypeFromDataset,
  getSearchRequestModelType,
} from '../../utils/dataset-utils.ts';
import type { FieldOption } from '../../utils/opensearch-field-mapper.ts';
import {
  DASHBOARD_REPORT_TYPE,
  DEFAULT_GRID,
  emptyBuildFilterConfig,
  emptyUserFilterConfig,
  isDashboardConfig,
  normalizeBuildFilterConfig,
  normalizeUserFilterConfig,
  normalizeWidgetLayouts,
  type BuildFilterConfig,
  type DashboardGridConfig,
  type UserFilterConfig,
  type WidgetConfig,
} from '../../utils/dashboard-builder/widget-config-types.ts';
import {
  drilldownFilterValues,
  drilldownKey,
  removeDrilldown,
  setDrilldown,
  summarizeDrilldown,
  toggleDrilldown,
  type Drilldown,
  type DrilldownMode,
} from '../../utils/dashboard-builder/drilldown.ts';
import ReportTrackingService from '../../services/report-tracking.ts';
import type FeaturesService from 'tio-common/services/features';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { createDebouncedResizeObserver } from 'tio-ui/utils/charts/resize-observer-utils';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type Owner from '@ember/owner';
import type { ViewedFrom } from 'tio-data/data/resources/report-tracking.type';

// DashboardViewPage – read-only dashboard render (mirrors report-view). Widgets
// are shown via WidgetGrid in non-editable mode (no drag/resize/edit). For
// single-dataset dashboards, build filters apply silently and viewers can
// adjust the creator-exposed user filters; their overrides persist to
// localStorage with a "Reset to saved" affordance. A report-tracking session
// runs for the lifetime of the view.

interface DashboardViewPageSignature {
  Args: {
    dashboard: Report;
    /**
     * The company the opening page acts for — the partner's own company, or the admin company
     * page's. Every query runs against it; a dashboard saved for a different company is refused.
     */
    companyId?: string;
    /** Label for `companyId`; looked up when omitted. */
    companyName?: string;
    /** `partner` never offers the company picker, whatever the dashboard's scope. */
    viewedFrom?: ViewedFrom;
    onBack?: () => void;
  };
  Element: HTMLDivElement;
}

function storageKey(id: string | null | undefined): string | undefined {
  return id ? `tio-dashboard-userfilters:${id}` : undefined;
}

export default class DashboardViewPage extends Component<DashboardViewPageSignature> {
  @service declare store: StoreService;
  @service declare reportTracking: ReportTrackingService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;

  @tracked widgets: WidgetConfig[] = [];
  grid: DashboardGridConfig = DEFAULT_GRID;
  /** The company the dashboard was built for; unset for a Global dashboard. */
  savedCompanyId?: string;
  savedCompanyName?: string;
  /** Picker choice on a Global dashboard; unset means all companies. */
  @tracked pickedCompanyId?: string;
  buildFilters: BuildFilterConfig = emptyBuildFilterConfig();
  savedUserFilters: UserFilterConfig = emptyUserFilterConfig();
  @tracked userFilters: UserFilterConfig = emptyUserFilterConfig();

  // Load-time snapshot for the distinct-values fetch (plain, untracked — read
  // inside dashboardFields without making viewer filter edits re-run it).
  // Build filters stay separate from user filters: they are hard constraints
  // that apply even to their own field's dropdown, while user filter values
  // cascade with self-exclusion.
  #distinctFetchContext: {
    keys: string[];
    buildFilterValues: Record<string, unknown>;
    buildFilterLayout: BuildFilterConfig['filterLayout'];
    userFilterValues: Record<string, unknown>;
  } = {
    keys: [],
    buildFilterValues: {},
    buildFilterLayout: [],
    userFilterValues: {},
  };

  constructor(owner: Owner, args: DashboardViewPageSignature['Args']) {
    super(owner, args);
    const cfg = args.dashboard?.searchConfig as unknown;
    if (isDashboardConfig(cfg)) {
      this.widgets = normalizeWidgetLayouts(cfg.widgets ?? []);
      this.grid = cfg.grid ?? DEFAULT_GRID;
      // Older configs stored the id as a number; compare as the URL carries it.
      this.savedCompanyId = cfg.companyId ? String(cfg.companyId) : undefined;
      this.savedCompanyName = cfg.companyName;
      this.buildFilters = normalizeBuildFilterConfig(cfg.buildFilters);
      this.savedUserFilters = normalizeUserFilterConfig(cfg.userFilters);
      this.userFilters = this.#loadUserFilters() ?? this.savedUserFilters;
      this.#distinctFetchContext = {
        keys: [...this.userFilters.selectedFilters],
        buildFilterValues: { ...this.buildFilters.filterValues },
        buildFilterLayout: [...this.buildFilters.filterLayout],
        userFilterValues: { ...this.userFilters.filterValues },
      };
    }

    const reportId = args.dashboard?.id;
    if (reportId && this.isAvailable) {
      const session = this.reportTracking.startSession(reportId, args.viewedFrom ?? 'admin');
      registerDestructor(this, () => session.end());
    }
  }

  #loadUserFilters(): UserFilterConfig | undefined {
    const key = storageKey(this.args.dashboard?.id);
    if (!key) return undefined;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return undefined;
      const persisted = normalizeUserFilterConfig(JSON.parse(raw));
      // The creator's exposed list is authoritative — viewers only override
      // values/operators. Reconcile per field: a field still in the persisted
      // list keeps the viewer's state (so intentional clears stay cleared),
      // while fields the persisted config predates — newly exposed, or removed
      // by the old view UI — inherit the creator's saved value/operator rather
      // than appearing blank. Keys for fields no longer exposed are dropped.
      const saved = this.savedUserFilters;
      const pickField = <T,>(src: Record<string, T>, field: string) =>
        Object.fromEntries(
          Object.entries(src).filter(([k]) => k === field || k.startsWith(`${field}__`))
        );
      const filterValues: UserFilterConfig['filterValues'] = {};
      const filterOperators: UserFilterConfig['filterOperators'] = {};
      for (const field of saved.selectedFilters) {
        const src = persisted.selectedFilters.includes(field) ? persisted : saved;
        Object.assign(filterValues, pickField(src.filterValues, field));
        Object.assign(filterOperators, pickField(src.filterOperators, field));
      }
      return { selectedFilters: [...saved.selectedFilters], filterValues, filterOperators };
    } catch {
      return undefined;
    }
  }

  #persistUserFilters(config: UserFilterConfig) {
    const key = storageKey(this.args.dashboard?.id);
    if (!key) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(config));
    } catch {
      // ignore quota / serialization failures
    }
  }

  get isDashboardReport() {
    return this.args.dashboard?.reportType === DASHBOARD_REPORT_TYPE;
  }

  get contextCompanyId() {
    return this.args.companyId ? String(this.args.companyId) : undefined;
  }

  /** Opened for one company, but built for another — never query across that boundary. */
  get isCompanyMismatch() {
    return Boolean(
      this.contextCompanyId && this.savedCompanyId && this.contextCompanyId !== this.savedCompanyId
    );
  }

  get isAvailable() {
    return this.isDashboardReport && !this.isCompanyMismatch;
  }

  /** The company every query runs against; unset only for a Global dashboard viewed across companies. */
  get companyId() {
    return this.contextCompanyId ?? this.savedCompanyId ?? this.pickedCompanyId;
  }

  get showCompanyPicker() {
    return (
      this.isAvailable &&
      !this.contextCompanyId &&
      !this.savedCompanyId &&
      this.args.viewedFrom !== 'partner'
    );
  }

  companiesData = trackedFunction(this, async () =>
    this.showCompanyPicker ? loadActiveCompanyOptions(this.store) : []
  );

  get companyPickerItems() {
    return [{ key: '', label: 'All companies' }, ...(this.companiesData.value ?? [])];
  }

  selectCompany = (key: string | null) => {
    const next = key || undefined;
    if (next === this.pickedCompanyId) return;
    this.pickedCompanyId = next;
    // Facet values belong to the previous company: drop them and any still in flight.
    this.#distinctFetchEpoch++;
    this.distinctValuesMap = {};
  };

  contextCompanyName = trackedFunction(this, async () => {
    const id = this.contextCompanyId;
    if (!id || this.args.companyName) return this.args.companyName;
    if (id === this.savedCompanyId && this.savedCompanyName) return this.savedCompanyName;
    const peeked = this.store.peekRecord('company', id) as CompanyModel | null;
    if (peeked?.name) return peeked.name;
    try {
      const { content } = (await this.store.request(findRecord<CompanyModel>('company', id))) as {
        content: { data?: CompanyModel };
      };
      return content.data?.name;
    } catch {
      return undefined;
    }
  });

  get formattedUpdatedAt() {
    const updatedAt = this.args.dashboard?.updatedAt;
    if (!updatedAt) return '';
    return new Date(updatedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  get datasets(): string[] {
    return [...new Set(this.widgets.map((w) => w.dataset))];
  }

  get isSingleDataset(): boolean {
    return this.datasets.length === 1;
  }

  get effectiveBuildFilters(): BuildFilterConfig {
    return this.isSingleDataset ? this.buildFilters : emptyBuildFilterConfig();
  }

  get effectiveUserFilters(): UserFilterConfig {
    return this.isSingleDataset ? this.userFilters : emptyUserFilterConfig();
  }

  /** Viewers only see the filter sidebar when the creator exposed any. */
  get userFilterCount(): number {
    return this.effectiveUserFilters.selectedFilters.length;
  }

  get showFilterSidebar(): boolean {
    return this.hasWidgets && this.userFilterCount > 0;
  }

  // ── collapsible filter sidebar (mirrors report-view) ─────────────────────
  @tracked isFilterSidebarOpen = true;

  toggleFilterSidebar = () => {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  };

  /** True when the viewer's filters differ from the saved config in a way that
   * changes the query. `''` / `[]` / null are "no constraint" (the clause
   * builder skips them), so placeholders left behind by operator round-trips
   * don't count, and operator changes only count for fields that carry an
   * effective value. */
  get isFilterDirty() {
    const isEmpty = (v: unknown) =>
      v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    // Multi-select emits set-equal arrays in varying order (deselect + reselect
    // re-appends), so compare arrays order-insensitively.
    const norm = (v: unknown) =>
      isEmpty(v) ? null : JSON.stringify(Array.isArray(v) ? [...v].sort() : v);
    const a = this.userFilters;
    const b = this.savedUserFilters;

    const valueKeys = new Set([...Object.keys(a.filterValues), ...Object.keys(b.filterValues)]);
    for (const k of valueKeys) {
      if (norm(a.filterValues[k]) !== norm(b.filterValues[k])) return true;
    }

    const hasEffectiveValue = (vals: Record<string, unknown>, fieldKey: string) =>
      Object.entries(vals).some(
        ([k, v]) => (k === fieldKey || k.startsWith(`${fieldKey}__`)) && !isEmpty(v)
      );
    const opKeys = new Set([...Object.keys(a.filterOperators), ...Object.keys(b.filterOperators)]);
    for (const k of opKeys) {
      if ((a.filterOperators[k] ?? null) === (b.filterOperators[k] ?? null)) continue;
      if (hasEffectiveValue(a.filterValues, k) || hasEffectiveValue(b.filterValues, k)) return true;
    }
    return false;
  }

  dashboardFields = trackedFunction(this, async () => {
    if (!this.isAvailable || !this.isSingleDataset) return [];
    const dataset = this.datasets[0]!;
    const individual = this.widgets[0]?.showIndividualApplicationsOnly ?? true;
    const fields = await resolveDatasetFields(this.store, dataset, this.companyId, individual);
    // Dropdown options. Uses the untracked user-filter snapshot (NOT
    // this.userFilters) so editing a viewer filter can't re-run this whole
    // resolver — reactive re-fetching goes through refreshDistinctValues.
    this.#fetchAllDistinctValues(fields, this.#distinctFetchContext.userFilterValues);
    return fields;
  });

  @tracked distinctValuesMap: Record<string, string[]> = {};

  /** Monotonic token: responses from a superseded query context are discarded. */
  #distinctFetchEpoch = 0;

  #fetchAllDistinctValues(fields: FieldOption[], userFilterValues: Record<string, unknown>) {
    if (!this.isSingleDataset) return;
    const dataset = this.datasets[0]!;
    const individual = this.widgets[0]?.showIndividualApplicationsOnly ?? true;
    const modelType = getSearchRequestModelType(getModelTypeFromDataset(dataset), individual);
    const epoch = ++this.#distinctFetchEpoch;
    // The widgets show why an unscoped SQL dataset can't run; its facets would only 4xx.
    if (sqlScopeUnavailable(modelType, this.companyId)) return;
    const companyFilterField = getCompanyFilterField(dataset);
    for (const key of this.#distinctFetchContext.keys) {
      const field = fields.find((f) => f.key === key);
      if (field)
        this.#resolveDistinctValues(
          field,
          modelType,
          fields,
          companyFilterField,
          userFilterValues,
          epoch
        );
    }
  }

  /** Re-fetch every facet against the CURRENT user selections (cascading). */
  refreshDistinctValues = () => {
    const fields = this.dashboardFields.value;
    if (fields) this.#fetchAllDistinctValues(fields, this.userFilters.filterValues);
  };

  #resolveDistinctValues(
    field: FieldOption,
    modelType: string,
    fields: FieldOption[],
    companyFilterField: string | undefined,
    userFilterValues: Record<string, unknown>,
    epoch: number
  ) {
    if (!shouldFetchDistinctValues(field)) return;
    fetchDistinctValues(
      this.store,
      field.key,
      this.companyId,
      {
        buildFilterValues: this.#distinctFetchContext.buildFilterValues,
        buildFilterLayout: this.#distinctFetchContext.buildFilterLayout,
        userFilterValues,
        fields,
        companyFilterField,
      },
      modelType,
      field.sortPath
    )
      .then((values) => {
        if (isDestroying(this) || isDestroyed(this)) return;
        // A newer refresh (or teardown) superseded this fetch — drop the result.
        if (epoch !== this.#distinctFetchEpoch) return;
        if (values) {
          this.distinctValuesMap = { ...this.distinctValuesMap, [field.key]: values };
        } else {
          // Over-cap/absent: remove so the field falls back to the full enum/text.
          const next = { ...this.distinctValuesMap };
          delete next[field.key];
          this.distinctValuesMap = next;
        }
      })
      .catch((error: unknown) => {
        // Non-fatal: the field falls back to a text input. Logged because a
        // silent fallback is indistinguishable from "this field can't be
        // grouped", which makes a missing dropdown undebuggable.
        console.warn(`Distinct values unavailable for "${field.key}":`, error);
      });
  }

  get dashboardFilterableFields() {
    return (this.dashboardFields.value ?? []).filter((f) => f.isFilterable);
  }

  get companyScopeLabel(): string {
    if (this.contextCompanyId) {
      return this.contextCompanyName.value ?? this.savedCompanyName ?? 'Selected company';
    }
    return this.savedCompanyName ?? 'All companies';
  }

  get hasWidgets(): boolean {
    return this.widgets.length > 0;
  }

  setUserFilters = (config: BuildFilterConfig | UserFilterConfig) => {
    const next = config as UserFilterConfig;
    this.#trackFilterChanges(this.userFilters, next);
    this.userFilters = next;
    this.#distinctFetchContext.userFilterValues = { ...next.filterValues };
    this.#persistUserFilters(next);
    // Cascade: the other facets' reachable values depend on this selection.
    this.refreshDistinctValues();
  };

  /** Records each changed filter value in the view's usage stats, labelled like the report viewer. */
  #trackFilterChanges(previous: UserFilterConfig, next: UserFilterConfig) {
    const keys = new Set([
      ...Object.keys(previous.filterValues),
      ...Object.keys(next.filterValues),
    ]);
    const fields = this.dashboardFields.value ?? [];
    for (const key of keys) {
      const value = next.filterValues[key];
      if (JSON.stringify(previous.filterValues[key]) === JSON.stringify(value)) continue;
      const base =
        next.selectedFilters.find((field) => key === field || key.startsWith(`${field}__`)) ?? key;
      this.reportTracking.trackFilter(key, value, fields.find((f) => f.key === base)?.label);
    }
  }

  resetUserFilters = () => {
    this.#trackFilterChanges(this.userFilters, this.savedUserFilters);
    this.userFilters = this.savedUserFilters;
    this.#distinctFetchContext.userFilterValues = { ...this.savedUserFilters.filterValues };
    const key = storageKey(this.args.dashboard?.id);
    if (key) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
    this.refreshDistinctValues();
  };

  // WidgetGrid requires a layout callback even when not editable.
  noop = () => {};

  // ── drilldowns (ephemeral; a clicked breakdown value filters the other widgets) ──
  @tracked drilldowns: Drilldown[] = [];

  get drilldownChips() {
    return this.drilldowns.map((d) => ({ key: drilldownKey(d), ...summarizeDrilldown(d) }));
  }

  applyDrilldown = (drilldown: Drilldown, mode: DrilldownMode = 'toggle') => {
    const apply = mode === 'set' ? setDrilldown : toggleDrilldown;
    const next = apply(this.drilldowns, drilldown);
    this.#trackDrilldownChange(this.drilldowns, next);
    this.drilldowns = next;
  };

  dismissDrilldown = (key: string) => {
    const next = removeDrilldown(this.drilldowns, key);
    this.#trackDrilldownChange(this.drilldowns, next);
    this.drilldowns = next;
  };

  clearDrilldowns = () => {
    this.#trackDrilldownChange(this.drilldowns, []);
    this.drilldowns = [];
  };

  /** Records each drilldown change in the view's usage stats like a filter change. */
  #trackDrilldownChange(previous: Drilldown[], next: Drilldown[]) {
    // Keyed per drilldown, so the same field on two datasets is two changes.
    const entries = (list: Drilldown[]) =>
      new Map(
        list.flatMap((d) =>
          Object.entries(drilldownFilterValues([d])).map(
            ([key, value]) =>
              [`${drilldownKey(d)}|${key}`, { key, value, label: d.fieldLabel }] as const
          )
        )
      );
    const before = entries(previous);
    const after = entries(next);
    for (const id of new Set([...before.keys(), ...after.keys()])) {
      const was = before.get(id);
      const now = after.get(id);
      if (JSON.stringify(was?.value) === JSON.stringify(now?.value)) continue;
      const entry = now ?? was;
      if (entry) this.reportTracking.trackFilter(entry.key, now?.value, entry.label);
    }
  }

  // ── widget CSV export (server export path, keyed on the dashboard report) ──
  // With no company picked on a Global dashboard the export runs across companies, as reports do.
  exportWidget = async (widget: WidgetConfig) => {
    const dashboardId = this.args.dashboard?.id;
    if (!dashboardId) return;
    try {
      const result = await exportWidgetCsv({
        store: this.store,
        apiKey: this.features.ENV?.apiKey ?? '',
        dashboardId,
        widget,
        companyId: this.companyId,
        buildFilters: this.effectiveBuildFilters,
        userFilters: this.effectiveUserFilters,
        drilldowns: this.drilldowns,
      });
      this.reportTracking.trackExport('csv');
      if (isDestroying(this) || isDestroyed(this)) return;
      if ('truncated' in result && result.truncated) {
        this.notifications.add(
          'Export truncated at the dataset’s export row cap — narrow the filters for a complete file.',
          { appearance: 'warning' }
        );
      }
    } catch (error) {
      console.error('Widget export failed:', error);
      if (isDestroying(this) || isDestroyed(this)) return;
      const message =
        error instanceof WidgetExportUnavailableError
          ? error.message
          : 'Could not download this widget’s data — please try again.';
      this.notifications.add(message, { appearance: 'error' });
    }
  };

  /** Counters (no single-value export path yet) and incomplete widgets have nothing to download. */
  canExportWidget = (widget: WidgetConfig) =>
    widgetExportDefinition(widget).selectedColumns.length > 0;

  exportActionFor = (widget: WidgetConfig) =>
    this.canExportWidget(widget) ? () => this.exportWidget(widget) : undefined;

  /** Why a widget's download stays inert: a layout that would lose a measure, or a SQL dataset that needs a company. */
  exportBlockedReasonFor = (widget: WidgetConfig) => {
    const blocker = widgetExportBlocker(widget);
    if (blocker) return blocker;
    const modelType = getSearchRequestModelType(
      getModelTypeFromDataset(widget.dataset),
      widget.showIndividualApplicationsOnly ?? true
    );
    return sqlScopeUnavailable(modelType, this.companyId) ? SQL_SCOPE_REQUIRED_MESSAGE : undefined;
  };

  get expandedExportBlockedReason() {
    return this.expandedWidget ? this.exportBlockedReasonFor(this.expandedWidget) : undefined;
  }

  get expandedExportDisabled() {
    return Boolean(this.expandedExportBlockedReason);
  }

  get expandedExportTitle() {
    return this.expandedExportBlockedReason ?? 'Download CSV';
  }

  get expandedExportLabel() {
    return `Download ${this.expandedWidget?.label || 'widget'} data as CSV`;
  }

  get canExportExpanded() {
    return Boolean(this.expandedWidget && this.canExportWidget(this.expandedWidget));
  }

  exportExpandedWidget = dropTask(async () => {
    const widget = this.expandedWidget;
    if (widget && this.canExportWidget(widget)) await this.exportWidget(widget);
  });

  exportExpanded = () => {
    void this.exportExpandedWidget.perform();
  };

  // ── expanded widget (takes over the viewport) ────────────────────────────────
  @tracked expandedWidgetId?: string;
  @tracked expandedBodyHeight = 480;

  get expandedWidget(): WidgetConfig | undefined {
    return this.widgets.find((w) => w.id === this.expandedWidgetId);
  }

  expandWidget = (id: string) => {
    this.expandedWidgetId = id;
  };

  collapseWidget = () => {
    this.expandedWidgetId = undefined;
  };

  /** Escape closes the expanded widget for the overlay's lifetime. */
  escapeToClose = modifier(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.collapseWidget();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  measureExpandedBody = modifier((element: HTMLElement) => {
    const observer = createDebouncedResizeObserver(() => {
      this.expandedBodyHeight = Math.max(240, element.clientHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  /** Chart height inside the overlay body (padding + legend allowance). */
  get expandedChartHeight() {
    return Math.max(240, this.expandedBodyHeight - 64);
  }

  <template>
    <div data-test-dashboard-view class="tio-col h-full min-h-0" ...attributes>
      <div class="tio-row-between gap-3 border-b tio-border-default tio-bg-surface px-6 py-4">
        <div class="tio-row">
          {{#if @onBack}}
            <button
              type="button"
              class="p-1 rounded hover:tio-bg-sunken tio-fg-muted"
              title="Back to dashboards"
              aria-label="Back to dashboards"
              data-test-dashboard-view-back
              {{on "click" @onBack}}
            >
              <PhArrowLeft @size={{20}} />
            </button>
          {{/if}}
          <PhSquaresFour @size={{24}} class="tio-fg-secondary" />
          <h1 class="tio-text-lg-semibold tio-fg-primary">{{@dashboard.name}}</h1>
          {{#if this.formattedUpdatedAt}}
            <span class="tio-type-body-secondary tio-fg-muted" data-test-dashboard-view-updated>
              Updated
              {{this.formattedUpdatedAt}}
            </span>
          {{/if}}
        </div>
        {{#if this.isAvailable}}
          {{#if this.showCompanyPicker}}
            <div class="min-w-[220px]" data-test-dashboard-view-company-picker>
              <Select
                @items={{this.companyPickerItems}}
                @selected={{if this.pickedCompanyId this.pickedCompanyId ""}}
                @onChange={{this.selectCompany}}
                @searchable={{true}}
                @size="sm"
                @placeholder="All companies"
                @ariaLabel="Company"
                @testHook="dashboard-view__company"
              />
            </div>
          {{else}}
            <span class="tio-type-body-secondary tio-fg-muted" data-test-dashboard-view-company>
              {{this.companyScopeLabel}}
            </span>
          {{/if}}
        {{/if}}
      </div>

      {{#if this.isAvailable}}
        <div class="relative min-h-0 flex-1">
          <div class="flex h-full">
            {{#if this.showFilterSidebar}}
              <div
                data-test-dashboard-user-filters
                class="shrink-0 border-r tio-border-default tio-bg-surface tio-col-flush transition-all duration-200
                  {{if this.isFilterSidebarOpen 'w-72' 'w-10'}}"
              >
                <div class="tio-row-between px-3 py-3 border-b tio-border-subtle">
                  {{#if this.isFilterSidebarOpen}}
                    <div class="tio-row-snug tio-text-sm-semibold tio-fg-secondary">
                      <PhFunnel @size={{14}} />
                      <span>Filters</span>
                    </div>
                  {{/if}}
                  <button
                    type="button"
                    class="p-1 rounded hover:tio-bg-sunken tio-fg-muted"
                    data-test-filter-sidebar-toggle
                    aria-label={{if this.isFilterSidebarOpen "Collapse filters" "Expand filters"}}
                    aria-expanded={{if this.isFilterSidebarOpen "true" "false"}}
                    {{on "click" this.toggleFilterSidebar}}
                  >
                    {{#if this.isFilterSidebarOpen}}
                      <PhCaretLeft @size={{14}} />
                    {{else}}
                      <PhSlidersHorizontal @size={{16}} />
                    {{/if}}
                  </button>
                </div>

                {{#if this.isFilterSidebarOpen}}
                  <div class="flex-1 overflow-y-auto p-3 space-y-4">
                    {{#if this.isFilterDirty}}
                      <button
                        type="button"
                        class="tio-inline-row gap-1.5 rounded-md border tio-border-warning tio-bg-warning-soft hover:tio-bg-warning-soft-hover px-2.5 py-1 tio-text-xs-medium tio-fg-warning-h"
                        data-test-dashboard-filter-reset-chip
                        {{on "click" this.resetUserFilters}}
                      >
                        <PhArrowCounterClockwise @size={{12}} />
                        Filters modified · Reset to saved
                      </button>
                    {{/if}}
                    <FilterEditor
                      @mode="user"
                      @allowAdd={{false}}
                      @fields={{this.dashboardFilterableFields}}
                      @config={{this.userFilters}}
                      @distinctValuesMap={{this.distinctValuesMap}}
                      @onChange={{this.setUserFilters}}
                      @buildFilterValues={{this.buildFilters.filterValues}}
                      @buildFilterLayout={{this.buildFilters.filterLayout}}
                    />
                  </div>
                {{/if}}
              </div>
            {{/if}}

            <div class="h-full min-w-0 flex-1 overflow-auto tio-bg-page p-4">
              <DrilldownChips
                class="mb-3"
                @chips={{this.drilldownChips}}
                @onDismiss={{this.dismissDrilldown}}
                @onClear={{this.clearDrilldowns}}
              />
              {{#if this.hasWidgets}}
                <WidgetGrid
                  @widgets={{this.widgets}}
                  @editable={{false}}
                  @companyId={{this.companyId}}
                  @buildFilters={{this.effectiveBuildFilters}}
                  @userFilters={{this.effectiveUserFilters}}
                  @userFiltersModified={{this.isFilterDirty}}
                  @drilldowns={{this.drilldowns}}
                  @onDrilldown={{this.applyDrilldown}}
                  @onUpdateLayout={{this.noop}}
                  @onExpand={{this.expandWidget}}
                  @exportActionFor={{this.exportActionFor}}
                  @exportBlockedReasonFor={{this.exportBlockedReasonFor}}
                />
              {{else}}
                <div
                  class="tio-row-center h-full tio-fg-muted text-sm"
                  data-test-dashboard-view-empty
                >
                  This dashboard has no widgets.
                </div>
              {{/if}}
            </div>
          </div>

          {{#if this.expandedWidget}}
            <div
              data-test-expanded-widget
              class="tio-col fixed inset-0 z-50 tio-bg-surface"
              {{this.escapeToClose}}
            >
              <header class="tio-row-between gap-2 border-b tio-border-subtle px-4 py-2.5">
                <div class="tio-col-flush min-w-0">
                  <span class="truncate tio-text-sm-semibold">{{this.expandedWidget.label}}</span>
                  {{#if this.expandedWidget.description}}
                    <span
                      class="truncate text-xs tio-fg-muted"
                    >{{this.expandedWidget.description}}</span>
                  {{/if}}
                </div>
                <span class="shrink-0 tio-row-extra-tight">
                  {{#if this.canExportExpanded}}
                    <Button
                      @testHook="dashboard-view__expanded-export"
                      @appearance="minimal"
                      @size="sm"
                      @icon={{PhDownloadSimple}}
                      @ariaLabel={{this.expandedExportLabel}}
                      @isRunning={{this.exportExpandedWidget.isRunning}}
                      @isDisabled={{this.expandedExportDisabled}}
                      title={{this.expandedExportTitle}}
                      data-test-expanded-export
                      {{on "click" this.exportExpanded}}
                    />
                  {{/if}}
                  <button
                    type="button"
                    data-test-expanded-close
                    class="tio-fg-muted hover:tio-fg-secondary"
                    aria-label="Close expanded widget"
                    {{on "click" this.collapseWidget}}
                  >✕</button>
                </span>
              </header>
              <DrilldownChips
                class="border-b tio-border-subtle px-4 py-2"
                @chips={{this.drilldownChips}}
                @onDismiss={{this.dismissDrilldown}}
                @onClear={{this.clearDrilldowns}}
              />
              <div class="min-h-0 flex-1 overflow-auto p-4" {{this.measureExpandedBody}}>
                <WidgetRenderer
                  @widget={{this.expandedWidget}}
                  @companyId={{this.companyId}}
                  @buildFilters={{this.effectiveBuildFilters}}
                  @userFilters={{this.effectiveUserFilters}}
                  @drilldowns={{this.drilldowns}}
                  @onDrilldown={{this.applyDrilldown}}
                  @height={{this.expandedChartHeight}}
                />
              </div>
            </div>
          {{/if}}
        </div>
      {{else}}
        <DashboardUnavailable />
      {{/if}}
    </div>
  </template>
}
